import { useTheme } from "@mui/material";
import Stack from "@mui/material/Stack";
import { CoverageDetails } from "@routing/Residents/components/CoverageDetails/CoverageDetails";
import Typography from "@mui/material/Typography";
import { Button, LoadingOverlay } from "@covertree/ui";
import useAppSelector from "@redux/hooks/useAppSelector";
import {
  saveExternalPolicyDocuments,
  selectExternalPolicyDocuments,
  selectProofOfCoverageDetails,
} from "@redux/coverages/coveragesSlice";
import { useState } from "react";
import { isCoverageDocument, moveBackInPathname } from "@utils";
import { useUploadCoverageDocumentsMutation } from "@redux/documents/documentsRestApi";
import useAppDispatch from "@redux/hooks/useAppDispatch";
import {
  createSearchParams,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { mapEventType } from "@utils/mapEventType/mapEventType";
import { DisplayPropertiesTable } from "@covertree/ui/content";
import { selectEventType } from "@redux/event/eventSlice";
import { useAddCoverageEventMutation } from "@redux/event/eventApi";
import { ADD_EVENT_ROUTES } from "@routing/Residents/ProofOfCoverage";

export function EventSummary() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();

  const [searchParams] = useSearchParams();
  const { coverageId, residentId } = useParams();

  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const [isUploadSuccess, setIsUploadSuccess] = useState(false);

  const coverage = useAppSelector(selectProofOfCoverageDetails);
  const eventType = useAppSelector(selectEventType);
  const files = useAppSelector(selectExternalPolicyDocuments);

  const [addCoverageEvent, { isLoading, isError, isSuccess }] =
    useAddCoverageEventMutation();
  const [uploadCoverageDocuments] = useUploadCoverageDocumentsMutation();

  const uploadDocuments = async (residentId: string, coverageId: string) => {
    if (files.length === 0) {
      setIsUploadSuccess(true);
      setIsUploadLoading(false);
      return;
    }

    const results = await Promise.all(
      files.map((file) =>
        uploadCoverageDocuments({
          residentId,
          coverageId,
          file,
        })
      )
    );

    const failedFiles = files.filter((file) => {
      const result = results.find((res) => res?.["data"]?.name === file.name);
      return !isCoverageDocument(result?.["data"]);
    });

    if (!!failedFiles.length) {
      dispatch(
        saveExternalPolicyDocuments({
          files: failedFiles,
          isFailed: true,
          coverageId,
        })
      );
    } else {
      setIsUploadSuccess(true);
    }
  };

  const onSubmit = async () => {
    setIsUploadLoading(true);

    const {
      agencyAddress,
      agencyEmail,
      agencyName,
      agencyPhoneNumber,
      annualCost,
      coverageDetails,
      insuranceCarrierName,
      isInternalSale,
      policyDescription,
      policyId,
      policyEffectiveDate,
      policyEndDate,
    } = coverage;

    await uploadDocuments(residentId, coverageId);

    await addCoverageEvent({
      coverageEvent: {
        agencyAddress,
        agencyEmail,
        agencyName,
        agencyPhoneNumber,
        annualCost,
        coverageDetails,
        insuranceCarrierName,
        isInternalSale,
        policyDescription,
        policyId,
        termEffectiveDate: policyEffectiveDate,
        termEndDate: policyEndDate,
        type: eventType,
      },
      residentId,
      coverageId,
    });
    setIsUploadLoading(false);
  };

  const isAnyLoading = isUploadLoading || isLoading;

  if (isAnyLoading) return <LoadingOverlay />;

  if (isError) {
    return (
      <Typography>
        Could not add the Coverage Event. Please check your internet connection
        or contact our team.
      </Typography>
    );
  }

  if (isSuccess && !isUploadLoading) {
    if (isUploadSuccess) {
      navigate(
        {
          pathname: `${moveBackInPathname(location.pathname, 1)}/${ADD_EVENT_ROUTES.EVENT_ADDED}`,
          search: createSearchParams(searchParams).toString(),
        },
        { replace: true }
      );
    } else {
      navigate(
        {
          pathname: `${moveBackInPathname(location.pathname, 1)}/${ADD_EVENT_ROUTES.EVENT_DOCUMENTS}`,
          search: createSearchParams(searchParams).toString(),
        },
        { replace: true }
      );
    }
    return <LoadingOverlay />;
  }

  return (
    <Stack gap={3}>
      <CoverageDetails coverage={coverage}>
        <DisplayPropertiesTable
          containerStyles={{
            padding: theme.spacing(3),
            width: "100%",
          }}
          properties={[
            {
              label: "Event Type",
              value: mapEventType(eventType),
            },
          ]}
        />
      </CoverageDetails>
      <Button
        data-testid="event-summary-submit-button"
        size="medium"
        onClick={onSubmit}>
        Looks Good, Submit
      </Button>
    </Stack>
  );
}
